/**
 * ═══════════════════════════════════════════════════════════════════════════════
 * GLOW ORB — Pulsing central orb with color shifts and particle trails
 * ═══════════════════════════════════════════════════════════════════════════════
 *
 * A central glowing circle/orb that responds to audio:
 *   - Size pulses with bass energy
 *   - Color shifts with frequency content
 *   - Subtle particle trails emit outward on beats
 *   - Inner ring of small dots orbit the orb
 *
 * HOW IT WORKS:
 * 1. Draw a radial gradient circle at canvas center.
 * 2. The gradient's radius is driven by bass energy + beat pulse.
 * 3. Hue shifts based on mid/treble balance.
 * 4. Small orbiting dots add visual interest.
 * 5. On beats, a ring "shockwave" expands outward and fades.
 */

import { Visualization, FrameData, VisualizationSetting } from '../../types';
import { BeatPulse, SmoothedValue } from '../../utils/audioUtils';
import { hsl } from '../../utils/colorUtils';

interface Ring {
  radius: number;
  alpha: number;
}

export class GlowOrb implements Visualization {
  id = 'glow-orb';
  name = 'Glow Orb';
  category = 'minimal' as const;
  description = 'Pulsing central orb with glow, color shifts, and ring shockwaves';
  renderer = 'canvas2d' as const;

  private canvas!: HTMLCanvasElement;
  private ctx!: CanvasRenderingContext2D;
  private beatPulse = new BeatPulse(0.92);
  private smoothSize = new SmoothedValue(0.1);
  private smoothHue = new SmoothedValue(0.04);
  private orbitAngle = 0;
  private rings: Ring[] = [];

  private baseSize = 0.15;  // fraction of min(width,height)
  private colorHue = 280;
  private orbitSpeed = 0.5;

  init(canvas: HTMLCanvasElement): void {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d')!;
  }

  render(frame: FrameData): void {
    const { bassEnergy, midEnergy, trebleEnergy, isBeat, beatIntensity, deltaTime } = frame;
    const { width, height } = this.canvas;
    const ctx = this.ctx;
    const cx = width / 2;
    const cy = height / 2;
    const minDim = Math.min(width, height);

    this.beatPulse.update(isBeat, beatIntensity);

    // Orb size pulses with bass
    const size = this.smoothSize.update(
      this.baseSize + bassEnergy * 0.08 + this.beatPulse.value * 0.06
    );
    const orbRadius = size * minDim;

    // Color reacts to frequency balance
    const hue = this.smoothHue.update(this.colorHue + midEnergy * 40 - trebleEnergy * 30);

    // Fade background
    ctx.fillStyle = 'rgba(0, 0, 0, 0.88)';
    ctx.fillRect(0, 0, width, height);

    // ── Expanding ring shockwaves on beat ───────────────────────────────
    if (isBeat && beatIntensity > 0.3) {
      this.rings.push({ radius: orbRadius, alpha: 0.6 });
    }
    // Update and draw rings
    for (let i = this.rings.length - 1; i >= 0; i--) {
      const ring = this.rings[i];
      ring.radius += deltaTime * 0.15;
      ring.alpha -= deltaTime * 0.0008;
      if (ring.alpha <= 0) {
        this.rings.splice(i, 1);
        continue;
      }
      ctx.beginPath();
      ctx.arc(cx, cy, ring.radius, 0, Math.PI * 2);
      ctx.strokeStyle = hsl(hue, 70, 60, ring.alpha);
      ctx.lineWidth = 1.5;
      ctx.stroke();
    }

    // ── Main orb glow (layered radial gradients for soft glow) ──────────
    // Outer glow
    const outerGlow = ctx.createRadialGradient(cx, cy, orbRadius * 0.5, cx, cy, orbRadius * 2.5);
    outerGlow.addColorStop(0, hsl(hue, 70, 50, 0.15 + this.beatPulse.value * 0.1));
    outerGlow.addColorStop(1, 'rgba(0, 0, 0, 0)');
    ctx.fillStyle = outerGlow;
    ctx.fillRect(0, 0, width, height);

    // Inner orb
    const innerGlow = ctx.createRadialGradient(cx, cy, 0, cx, cy, orbRadius);
    innerGlow.addColorStop(0, hsl(hue, 80, 70, 0.9));
    innerGlow.addColorStop(0.5, hsl(hue, 70, 55, 0.6));
    innerGlow.addColorStop(1, hsl(hue, 60, 40, 0));
    ctx.fillStyle = innerGlow;
    ctx.beginPath();
    ctx.arc(cx, cy, orbRadius, 0, Math.PI * 2);
    ctx.fill();

    // ── Orbiting dots ───────────────────────────────────────────────────
    this.orbitAngle += (this.orbitSpeed * deltaTime) / 1000;
    const dotCount = 8;
    const orbitRadius = orbRadius * 1.4;
    for (let i = 0; i < dotCount; i++) {
      const angle = this.orbitAngle + (i / dotCount) * Math.PI * 2;
      const dx = cx + Math.cos(angle) * orbitRadius;
      const dy = cy + Math.sin(angle) * orbitRadius;
      const dotSize = 2 + midEnergy * 3;

      ctx.beginPath();
      ctx.arc(dx, dy, dotSize, 0, Math.PI * 2);
      ctx.fillStyle = hsl(hue + i * 30, 70, 65, 0.6 + this.beatPulse.value * 0.3);
      ctx.fill();
    }
  }

  resize(width: number, height: number): void {
    this.canvas.width = width;
    this.canvas.height = height;
  }

  destroy(): void {
    this.rings = [];
  }

  getSettings(): VisualizationSetting[] {
    return [
      { key: 'baseSize', label: 'Orb Size', type: 'range', value: this.baseSize, min: 0.05, max: 0.3, step: 0.01 },
      { key: 'colorHue', label: 'Color', type: 'range', value: this.colorHue, min: 0, max: 360, step: 10 },
      { key: 'orbitSpeed', label: 'Orbit Speed', type: 'range', value: this.orbitSpeed, min: 0, max: 2, step: 0.1 },
    ];
  }

  setSettings(settings: Record<string, number | string | boolean>): void {
    if (typeof settings.baseSize === 'number') this.baseSize = settings.baseSize;
    if (typeof settings.colorHue === 'number') this.colorHue = settings.colorHue;
    if (typeof settings.orbitSpeed === 'number') this.orbitSpeed = settings.orbitSpeed;
  }
}
